import { Injectable } from "@angular/core";
import { ObgConfigFeAdaptiveClientCommon } from "@obg/common.config";
import { ObgConfigFeAdaptiveClientCore } from "@obg/core.config";
import { MarketingOffers } from "@obg/ngx.core";
import { ConfigService, fromMinutesToMilliseconds, fromSecondsToMilliseconds } from "@obg/ngx.global";

import { CountrySettings, PhoneValidationSettings, ProvinceSettings } from "./registration.model";

const CUSTOMER_REGISTRATION_TOKENS_ENDPOINT = "v1/customerregistrationtokens";
const CUSTOMER_REGISTRATION_VERIFICATION_ENDPOINT = "v1/customerregistrationverification";
const USERNAME_CHECK_ENDPOINT = "v1/credentials/up";
const DATA_LOOKUP_LEVEL_ENDPOINT = "v1/personal-data-level";
const ACCOUNT_UNIQUENESS_ENDPOINT = "v1/accounts";

@Injectable({
	providedIn: "root",
})
export class RegistrationConfigService {
	constructor(private service: ConfigService<ObgConfigFeAdaptiveClientCommon & ObgConfigFeAdaptiveClientCore>) {}

	getRegistrationTokensEndpoint(country = ""): string {
		const url = this.service.get((x) => x.common.registration.endpoints!.registrationTokens, undefined);

		if (country === "" || !url?.countrySpecific || !Object.keys(url?.countrySpecific).includes(country)) {
			return url?.default ?? CUSTOMER_REGISTRATION_TOKENS_ENDPOINT;
		}

		return url.countrySpecific[country];
	}

	getDataLookupLevelEndpoint(): string {
		return this.service.get((x) => x.common.registration.endpoints!.dataLookUpLevel, DATA_LOOKUP_LEVEL_ENDPOINT);
	}

	getAccountUniquenessEndpoint(): string {
		return this.service.get((x) => x.common.registration.endpoints!.accountUniqueness, ACCOUNT_UNIQUENESS_ENDPOINT);
	}

	getRegistrationUsernameCheckEndpoint(): string {
		return this.service.get((x) => x.common.registration.endpoints!.usernameCheck, USERNAME_CHECK_ENDPOINT);
	}

	getRegistrationStatusEndpoint(registrationToken: string): string {
		return this.service.get(
			(x) => x.common.registration.endpoints!.registrationStatus,
			`${CUSTOMER_REGISTRATION_TOKENS_ENDPOINT}/${registrationToken}`
		);
	}

	getRegisterEndpoint(country = ""): string {
		const url = this.service.get((x) => x.common.registration.endpoints!.register, undefined);

		if (country === "" || !url?.countrySpecific || !Object.keys(url?.countrySpecific).includes(country)) {
			return url?.default ?? CUSTOMER_REGISTRATION_TOKENS_ENDPOINT;
		}

		return url.countrySpecific[country];
	}

	getRegistrationVerificationsEndpoint(): string {
		return this.service.get(
			(x) => x.common.registration.endpoints!.registrationVerifications,
			CUSTOMER_REGISTRATION_VERIFICATION_ENDPOINT
		);
	}

	getRegistrationTokenEndpoint(): string {
		return this.service.get(
			(x) => x.common.registration.endpoints!.registrationToken,
			`${CUSTOMER_REGISTRATION_VERIFICATION_ENDPOINT}/:registrationToken`
		);
	}

	getRegistrationStatusInterval(): number {
		return this.service.get(
			(x) => x.common.registration.registrationStatusInterval,
			fromSecondsToMilliseconds(1.5)
		);
	}

	getRegistrationStatusTimeout(): number {
		return this.service.get((x) => x.common.registration.registrationStatusTimeout, fromMinutesToMilliseconds(5));
	}

	getMinUserAge(): number {
		return this.service.get((x) => x.common.registration.minUserAge, 18);
	}

	isRegistrationEnabled(): boolean {
		return this.service.get((x) => x.common.registration.isRegistrationEnabled, true);
	}

	getMinUserAgeByMarket(): Record<string, number> {
		return this.service.get((x) => x.common.registration.minUserAgeByMarket, {});
	}

	getPermittedCountries(): string[] {
		return this.service.get((x) => x.common.registration.permittedCountries, []);
	}

	getForbiddenCountries(): string[] {
		return this.service.get((x) => x.common.registration.forbiddenCountries, []);
	}

	getIncomeAccessDataTrackingCountries(): string[] {
		return this.service.get((x) => x.common.registration.incomeAccessDataTrackingCountries, []);
	}

	getRedirectCountries(): Record<string, string> {
		return this.service.get((x) => x.common.registration.redirectCountries, {});
	}

	getNorthStarForbiddenCountries(): string[] {
		return this.service.get((x) => x.common.registration.northStarForbiddenCountries, []);
	}

	getNativeRedirectionExcludedCountries(): string[] {
		return this.service.get((x) => x.common.registration.nativeRedirectionExcludedCountries, []);
	}

	getCountriesWithAlternateIcons(): string[] {
		return this.service.get((x) => x.common.registration.countriesWithAlternateIcons, []);
	}

	getMarketingOffers(): MarketingOffers {
		return {
			acceptEmailOffers: true,
			acceptSmsOffers: true,
			acceptTelesalesOffers: true,
			...this.service.get<Partial<MarketingOffers>>((x) => x.common.registration.marketingOffers, {}),
		};
	}

	getMarketingOffersEnabled(): boolean {
		return this.service.get((x) => x.common.registration.isMarketingOffersEnabled, false);
	}

	getDepositLimitEnabled(): boolean {
		return this.service.get((x) => x.common.registration.isDepositLimitEnabled, false);
	}

	getUppercaseEnabled(): boolean {
		return this.service.get((x) => x.common.registration.inputUppercaseIsEnabled, false);
	}

	getGreekToLatinEnabled(): boolean {
		return this.service.get((x) => x.common.registration.inputGreekToLatinIsEnabled, false);
	}

	getWhiteSpaceTrimEnabled(): boolean {
		return this.service.get((x) => x.common.registration.inputWhiteSpaceTrimIsEnabled, false);
	}

	getWhiteSpaceToHyphenEnabled(): boolean {
		return this.service.get((x) => x.common.registration.inputWhiteSpaceToHyphenIsEnabled, false);
	}

	getCurrencySelectorEnabled(): boolean {
		return this.service.get((x) => x.common.registration.isCurrencySelectorEnabled, true);
	}

	getNationalIdNumberEnabled(): boolean {
		return this.service.get((x) => x.common.registration.isNationalIdNumberEnabled, false);
	}

	getUsernameEnabled(): boolean {
		return this.service.get((x) => x.common.registration.isUsernameEnabled, false);
	}

	getIsAsciiValidationEnabled(): boolean {
		return this.service.get((x) => x.common.registration.isAsciiValidationEnabled, false);
	}

	getDefaultPhoneValidationSettings(): PhoneValidationSettings {
		return this.service.get((x) => x.common.registration.defaultPhoneValidationSettings, {
			minLength: 6,
			maxLength: 15,
			placeholderKey: "register.phone",
		});
	}

	getCountrySettings(): Record<string, CountrySettings> {
		return this.service.get((x) => x.common.registration.countrySettings, {});
	}

	getProvinceSettings(countryCode: string): ProvinceSettings {
		const countrySettings = this.getCountrySettings()[countryCode?.toLocaleLowerCase()];
		return {
			countryCode,
			autoSelectProvince: countrySettings?.addressSettings?.autoSelectProvince ?? false,
			hasCityList: countrySettings?.addressSettings?.hasCityList ?? false,
			showCityMismatchError: countrySettings?.addressSettings?.showCityMismatchError ?? false,
			showProvinceBeforeCountry: countrySettings?.addressSettings?.showProvinceBeforeCountry ?? false,
			cityHasProvinceCode: countrySettings?.addressSettings?.cityHasProvinceCode ?? false,
			fetchProvincesFromContent: countrySettings?.addressSettings?.fetchProvincesFromContent ?? false,
			getStateShortFormFromGooglePlaces:
				countrySettings?.addressSettings?.getStateShortFormFromGooglePlaces ?? false,
		};
	}

	getCityHasProvinceCode(countryCode: string): boolean {
		const countrySettings = this.getCountrySettings()[countryCode?.toLocaleLowerCase()];
		return countrySettings?.addressSettings?.hasCityList ?? false;
	}

	getNativePersonNameEnabled(countryCode: string): boolean {
		const countrySetting = this.getCountrySettings()[countryCode?.toLocaleLowerCase()];
		return countrySetting?.isNativePersonNameEnabled ?? false;
	}

	getIgnoreNativeLanguageCheck(): boolean {
		return this.service.get((x) => x.common.registration.ignoreNativeLanguageCheck, false);
	}

	getIsCurrencyLocked(countryCode: string): boolean {
		const countrySetting = this.getCountrySettings()[countryCode?.toLocaleLowerCase()];
		return countrySetting?.isCurrencyLocked ?? false;
	}

	getPhoneValidationSettingsByCallingCode(): Record<string, PhoneValidationSettings> {
		return this.service.get((x) => x.common.registration.phoneValidationSettings, {});
	}

	getQuickRegisterCountries(): string[] {
		return this.service.get((x) => x.common.registration.quickRegisterCountries, []);
	}

	getRegistrationFormTitlesEnabled(): boolean {
		return this.service.get((x) => x.common.registration.showRegistrationFormTitles, false);
	}

	isLoginNudgeEnabled(): boolean {
		return this.service.get((x) => x.common.registration.accountAlreadyExistNudge, false);
	}

	isSortOccupationOptionsByValueEnabled(): boolean {
		return this.service.get((x) => x.common.registration.sortOccupationOptionsByValue, false);
	}

	getIsCountryAutocompleteEnabled(): boolean {
		return this.service.get((x) => x.common.registration.isCountryAutocompleteEnabled, false);
	}

	/**
	 * This config is under jurisdiction.
	 */
	isChangeJurisdictionButtonEnabled(): boolean {
		return this.service.get((x) => x.common.registration.isChangeJurisdictionButtonEnabled, false);
	}

	isSgaEnabled(): boolean {
		return this.service.get((x) => x.common.isSgaEnabled, false);
	}

	getMarketingConsentsOptOutCountries(): string[] {
		return this.service.get((x) => x.common.registration.marketingConsentsOptOutCountries, []);
	}

	getProvinceCountries(): string[] {
		return this.service.get((x) => x.common.registration.provinceCountries, []);
	}

	getBlockedProvinces(): string[] {
		return this.service.get((x) => x.common.registration.blockedProvinces, []);
	}

	getIsOntarioNotificationEnabled(): boolean {
		return this.service.get((x) => x.common.registration.isOntarioNotificationEnabled, false);
	}

	getZipCodeOptionalCountries(): string[] {
		return this.service.get((x) => x.common.registration.zipCodeOptionalCountries, []);
	}

	getIsCapturePageEnabled(): boolean {
		return this.service.get((x) => x.common.registration.isCapturePageEnabled, false);
	}

	getIdinRegistrationUrl(): string {
		return this.service.get((x) => x.common.registration.endpoints?.idinRegistrationUrl, "");
	}

	getIdinCapturePageUrl(): string {
		return this.service.get((x) => x.common.registration.endpoints?.idinCapturePageUrl, "");
	}

	getIbanVerificationEnabled(): boolean {
		return this.service.get((x) => x.common.registration.isIbanVerificationEnabled, false);
	}

	getIbanVerificationUrl(): string {
		return this.service.get((x) => x.common.registration.endpoints?.ibanVerificationUrl, "");
	}

	getIsSignicatServiceEnabled(): boolean {
		return this.service.get((x) => x.common.registration.isSignicatServiceEnabled, false);
	}

	getAddressByCepCodeEndpoint(): string {
		return this.service.get((x) => x.common.registration.endpoints?.getAddressByCepCode, "v1/addresses");
	}

	getAddressesEndpoint(): string {
		return this.service.get((x) => x.common.registration.endpoints?.getAddressByCepCode, "v1/addresses");
	}

	getIsAddressByCepCodeEnabled(): boolean {
		return this.service.get((x) => x.common.registration.isAddressByCepCodeEnabled, false);
	}

	getIsCpfNumberEnabled(): boolean {
		return this.service.get((x) => x.common.registration.isCpfNumberEnabled, false);
	}

	getCepCodeHelperUrl(): string {
		return this.service.get(
			(x) => x.common.registration.endpoints?.getCepCodeHelperUrl,
			"https://buscacepinter.correios.com.br/app/endereco/index.php"
		);
	}

	getThumbnailImagesEnabled(): boolean {
		return this.service.get((x) => x.common.registration.isThumbnailImagesEnabled, true);
	}

	getEmailValidationEndpoint(): string {
		return this.service.get((x) => x.common.registration.endpoints?.emailValidationEndpoint, "");
	}

	getPhoneNumberValidationEndpoint(): string {
		return this.service.get((x) => x.common.registration.endpoints?.phoneNumberValidationEndpoint, "");
	}

	getIsCpfFormEnabled(): boolean {
		return this.service.get((x) => x.common.registration.isCpfFormEnabled, false);
	}

	getIsBrFormEnabled(): boolean {
		return this.service.get((x) => x.common.registration.isBrFormEnabled, false);
	}

	getAddressLookupEnabled(): boolean {
		return this.service.get((x) => x.common.registration.isAddressLookupEnabled, false);
	}

	getPlacesApiKey(): string {
		return this.service.get((x) => x.common.registration.googlePlaces?.apiKey, "");
	}

	getBankIdRegistrationUrl(): string {
		return this.service.get((x) => x.common.registration.endpoints?.bankIdRegistrationUrl, "");
	}

	getSPIDAuthorizationUrl(): string {
		const baseUrl = window.location.origin;
		const endpoint = this.service.get(
			(x) => x.common.registration.endpoints?.spIdAuthorizationUrl,
			"/oidc/authentication/namirialWithSpid/21"
		);

		return `${baseUrl}${endpoint}`;
	}

	getSPIDJwtUrl(): string {
		const baseUrl = window.location.origin;
		const endpoint = this.service.get(
			(x) => x.common.registration.endpoints?.spIdJwtTokenUrl,
			"/oidc/personData/namirial/21"
		);

		return `${baseUrl}${endpoint}`;
	}

	getDepartments(): string[] {
		return this.service.get((x) => x.common.registration.getDepartments, []);
	}

	getMunicipalities(): string[] {
		return this.service.get((x) => x.common.registration.getMunicipalities, []);
	}

	getNorthStarRegistrationEnabled(): boolean {
		return this.service.get((x) => x.common.registration.isNorthStarRegistrationEnabled, false);
	}

	getPromotionBannerEnabled(): boolean {
		return this.service.get((x) => x.common.registration.isPromotionBannerEnabled, false);
	}

	getNorthStarRegistrationExcludedCountries(): string[] {
		return this.service.get((x) => x.common.registration.northStarExcludedCountries, []);
	}

	getIsAutomaticLoginDisabledAfterSuccessfulRegistration() {
		return this.service.get(
			(x) => x.common.registration.isAutomaticLoginDisabledAfterSuccessfulRegistration,
			false
		);
	}

	getExclusiveCurrencyList() {
		return this.service.get((x) => x.common.registration.exclusiveCurrencyList, []);
	}

	getBankIdAppNotOpenedTimeout(): number {
		return this.service.get((x) => x.common.registration.bankIdAppNotOpenedTimeout, fromSecondsToMilliseconds(5));
	}

	getBrPersonalDataEndpoint(): string {
		return this.service.get((x) => x.common.registration.endpoints?.brPersonalDataEndpoint, "");
	}

	getNativeRedirectionDomain(): string {
		return this.service.get((x) => x.core.nativeRoute.routes?.redirectionDomain, "");
	}

	getEmailAvailabilityCheckEnabled(): boolean {
		return this.service.get((x) => x.common.registration.emailAvailabilityCheckEnabled, false);
	}

	getPhoneAvailabilityCheckEnabled(): boolean {
		return this.service.get((x) => x.common.registration.phoneAvailabilityCheckEnabled, false);
	}

	getEmailSuffixes(): string[] {
		return this.service.get((x) => x.common.registration.emailSuffixes, []);
	}

	getSPIDEnabled(): boolean {
		return this.service.get((x) => x.common.registration.isSPIDEnabled, false);
	}

	getIsPaxRnsEnabled(): boolean {
		return this.service.get((x) => x.common.registration.isPaxRnsEnabled, false);
	}

	getDynamicFormConfig(): Record<string, any> | undefined {
		return this.service.get((x) => x.common.registration.dynamicFormConfig, undefined);
	}
}
